import { useEffect, useRef } from 'react'
import websocket from '@/utils/websocket'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import { useCrmIntegrationChatUserAccess } from '@/hooks/useCrmIntegrationChatUserAccess'
import debounce from '@/utils/debounce/debounce'
import {
  CRM_REALTIME_CHAT_REFETCH_DEBOUNCE_MS,
  isIntegrationChannelDeletedWsMessage,
  isIntegrationMessageDeletedWsMessage,
  isIntegrationMessageInsertedWsMessage,
  isIntegrationMessageReadWsMessage,
  isVkMessageInsertedWsMessage,
  isVkMessageReadWsMessage,
} from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'

type UseCrmIntegrationChatsListRealtimeRefetchParams = {
  enabled?: boolean
  silentRetry: () => void
  onSync?: () => void
  /** Optimistic: conversation unread → 0 по integration_message_read */
  clearConversationUnread?: (conversationId: number) => void
}

/** WS integration_message_* / integration_channel_deleted / vk_message_* → тихий refetch списка CRM-чатов. */
export const useCrmIntegrationChatsListRealtimeRefetch = ({
  enabled = true,
  silentRetry,
  onSync,
  clearConversationUnread,
}: UseCrmIntegrationChatsListRealtimeRefetchParams): void => {
  const { isLoading, hasAnyConnected } = useCrmIntegrationChatAvailability()
  const hasUserAccess = useCrmIntegrationChatUserAccess()

  const silentRetryRef = useRef(silentRetry)
  silentRetryRef.current = silentRetry

  const onSyncRef = useRef(onSync)
  onSyncRef.current = onSync

  const clearConversationUnreadRef = useRef(clearConversationUnread)
  clearConversationUnreadRef.current = clearConversationUnread

  const scheduleChatsListRefetchRef = useRef(
    debounce(() => {
      silentRetryRef.current()
      onSyncRef.current?.()
    }, CRM_REALTIME_CHAT_REFETCH_DEBOUNCE_MS),
  )

  const canSyncChatsList =
    enabled &&
    !isLoading &&
    hasAnyConnected &&
    hasUserAccess

  useEffect(() => {
    if (!canSyncChatsList) {
      return
    }

    websocket.connect()

    const onWs = (message: unknown) => {
      if (isIntegrationMessageReadWsMessage(message)) {
        clearConversationUnreadRef.current?.(
          Number(message.data.payload.conversation_id),
        )
        scheduleChatsListRefetchRef.current()
        return
      }

      if (
        isIntegrationMessageDeletedWsMessage(message) ||
        isIntegrationChannelDeletedWsMessage(message)
      ) {
        scheduleChatsListRefetchRef.current()
        return
      }

      if (
        !isIntegrationMessageInsertedWsMessage(message) &&
        !isVkMessageInsertedWsMessage(message) &&
        !isVkMessageReadWsMessage(message)
      ) {
        return
      }

      scheduleChatsListRefetchRef.current()
    }

    const unsubscribeIntegration = websocket.subscribe(
      'integration_message_inserted',
      onWs,
    )
    const unsubscribeIntegrationRead = websocket.subscribe(
      'integration_message_read',
      onWs,
    )
    const unsubscribeIntegrationDeleted = websocket.subscribe(
      'integration_message_deleted',
      onWs,
    )
    const unsubscribeChannelDeleted = websocket.subscribe(
      'integration_channel_deleted',
      onWs,
    )
    const unsubscribeInserted = websocket.subscribe(
      'vk_message_inserted',
      onWs,
    )
    const unsubscribeRead = websocket.subscribe('vk_message_read', onWs)

    return () => {
      unsubscribeIntegration?.()
      unsubscribeIntegrationRead?.()
      unsubscribeIntegrationDeleted?.()
      unsubscribeChannelDeleted?.()
      unsubscribeInserted?.()
      unsubscribeRead?.()
    }
  }, [canSyncChatsList])
}
